import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { TagBadge } from './TagBadge';
import type { CampaignTag } from '../../types/campaign';
import { getTags } from '../../services/campaignTags';
import { bulkApplyTags } from '../../services/campaigns';
import toast from 'react-hot-toast';

interface BulkTagModalProps {
  campaignIds: number[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkTagModal: React.FC<BulkTagModalProps> = ({
  campaignIds,
  open,
  onClose,
  onSuccess,
}) => {
  const [tags, setTags] = useState<CampaignTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [action, setAction] = useState<'add' | 'remove' | 'replace'>('add');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      loadTags();
      setSelectedTagIds([]);
      setAction('add');
    }
  }, [open]);

  const loadTags = async () => {
    setIsLoading(true);
    try {
      const data = await getTags();
      setTags(data);
    } catch (error) {
      console.error('Failed to load tags:', error);
      toast.error('Failed to load tags');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTag = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedTagIds.length === 0) {
      toast.error('Please select at least one tag');
      return;
    }

    setIsSubmitting(true);
    try {
      await bulkApplyTags(campaignIds, selectedTagIds, action);
      toast.success(`Tags ${action}ed successfully`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to apply tags:', error);
      const errorMessage = error.response?.data?.message || 'Failed to apply tags';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Apply Tags to Selected Campaigns">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-gray-50 p-3 rounded-md">
          <p className="text-sm text-gray-600">
            Applying tags to {campaignIds.length} campaign{campaignIds.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value as 'add' | 'remove' | 'replace')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            disabled={isSubmitting}
          >
            <option value="add">Add tags to existing tags</option>
            <option value="remove">Remove tags from campaigns</option>
            <option value="replace">Replace all tags with selected tags</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Tags</label>
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading tags...</p>
          ) : tags.length === 0 ? (
            <p className="text-sm text-gray-500">No tags available. Create tags first.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <label key={tag.id} className="cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTagIds.includes(tag.id)}
                    onChange={() => handleToggleTag(tag.id)}
                    className="sr-only"
                    disabled={isSubmitting}
                  />
                  <div
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      selectedTagIds.includes(tag.id)
                        ? 'ring-2 ring-offset-2'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{
                      backgroundColor: `${tag.color}20`,
                      color: tag.color,
                      border: `1px solid ${tag.color}40`,
                      ringColor: tag.color,
                    }}
                  >
                    {selectedTagIds.includes(tag.id) && (
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                    {tag.name}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || selectedTagIds.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Applying...' : 'Apply Tags'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

BulkTagModal.displayName = 'BulkTagModal';
